/**
 * Footer Component
 *
 * Landing page footer with:
 * - Navigation links (Discover, Company)
 * - Logo
 * - Social media links
 * - Contact support button
 * - Language selector
 * - Copyright notice
 */

'use client';

import Link from 'next/link';
import { useTranslations, useLocale } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { Container } from '@/src/shared/ui/Container';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { PrimaryButton } from '@/src/shared/ui/button/PrimaryButton';
import { Logo } from '@/src/shared/ui/Logo';
import { LanguageSelector } from '@/src/features/language-selector';
import { useModalStore } from '@/src/shared/store/modal.store';

interface FooterLink {
  key: string;
  href: string;
  highlight?: boolean;
}

const discoverLinksConfig: FooterLink[] = [
  { key: 'categories', href: '/categories' },
  { key: 'howItWorks', href: '/how-it-works' },
];

const companyLinksConfig: FooterLink[] = [
  { key: 'terms', href: '/legal/terms' },
  { key: 'privacy', href: '/legal/privacy' },
  { key: 'cookies', href: '/legal/cookies' },
];

const socialLinks = [
  {
    name: 'instagram',
    href: 'https://instagram.com/hummii',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
  },
  {
    name: 'x',
    href: 'https://x.com/hummii',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
  },
  {
    name: 'facebook',
    href: 'https://facebook.com/hummii',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
  },
];

export function Footer() {
  const t = useTranslations('landing.footer');
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const openModal = useModalStore((s) => s.openModal);

  const availableLocales = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
  ];

  const handleContactSupport = () => {
    console.log('Contact support clicked');
    // Navigate to support page or open contact modal
  };

  const switchLocale = (newLocale: string) => {
    const pathWithoutLocale = pathname.replace(`/${locale}`, '');
    const newPathname = `/${newLocale}${pathWithoutLocale}`;
    router.push(newPathname);
  };

  return (
    <footer className="bg-background-primary border-t border-background-secondary py-12 md:py-16">
      <Container maxWidth="xl">
        {/* Main Footer Content */}
        {/* Mobile & Tablet: 4 columns, Desktop: 5 columns (logo in center) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-8 md:gap-6 mb-8">
          {/* Discover Column */}
          <div className="space-y-4">
            <Typography variant="h3" weight="medium" color="secondary">
              {t('discover')}
            </Typography>
            <nav className="space-y-3">
              {discoverLinksConfig.map((link) => (
                <Link
                  key={link.href}
                  href={`/${locale}${link.href}`}
                  className="block text-text-primary hover:text-accent-primary transition-colors"
                >
                  <Typography variant="body" color="primary">
                    {t(`links.${link.key}`)}
                  </Typography>
                </Link>
              ))}
              <button
                onClick={() => openModal('post-task')}
                className="block text-left text-text-primary hover:text-accent-primary transition-colors"
              >
                <Typography variant="body" color="primary">
                  {t('links.postTask')}
                </Typography>
              </button>
              <button
                onClick={() => openModal('registration', { intent: { intendedRole: 'CONTRACTOR' } })}
                className="block text-left text-accent-primary hover:text-accent-hover font-medium transition-colors"
              >
                <Typography variant="body" color="accent">
                  {t('links.becomeTasker')}
                </Typography>
              </button>
            </nav>
          </div>

          {/* Company Column */}
          <div className="space-y-4">
            <Typography variant="h3" weight="medium" color="secondary">
              {t('company')}
            </Typography>
            <nav className="space-y-3">
              {companyLinksConfig.map((link) => (
                <Link
                  key={link.href}
                  href={`/${locale}${link.href}`}
                  className="block text-text-primary hover:text-accent-primary transition-colors"
                >
                  <Typography variant="body" color="primary">
                    {t(`links.${link.key}`)}
                  </Typography>
                </Link>
              ))}
            </nav>
          </div>

          {/* Logo Column (Center on Desktop only, bottom on Mobile/Tablet) */}
          <div className="hidden xl:flex items-start justify-center">
            <Link href="/" className="inline-block">
              <img src="/images/logo/name.svg" alt="Hummii Logo" className="h-10 md:h-12" />
             {/*
              <Logo variant="fullColor" className="h-10 md:h-12" />
             */}
            </Link>
          </div>

          {/* Follow Us Column */}
          <div className="space-y-4">
            <Typography variant="h3" weight="medium" color="secondary">
              {t('followUs')}
            </Typography>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-primary hover:text-accent-primary transition-colors"
                  aria-label={t(`social.${social.name}`)}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Need Help Column */}
          <div className="space-y-4">
            <Typography variant="h3" weight="medium" color="secondary">
              {t('needHelp')}
            </Typography>
            <div className="space-y-4">
              <PrimaryButton
                onClick={handleContactSupport}
                className="w-full sm:w-auto px-6"
              >
                <Typography variant="body" color="inherit" weight="medium">
                  {t('contactSupport')}
                </Typography>
              </PrimaryButton>

              {/* Language Selector */}
              <div>
                <Typography variant="bodySm" color="secondary" className="mb-2">
                  {t('language')}
                </Typography>
                <LanguageSelector
                  currentLocale={locale}
                  availableLocales={availableLocales}
                  onLocaleChange={switchLocale}
                  label={t('language')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Logo at bottom for Mobile and Tablet */}
        <div className="xl:hidden flex justify-center mb-8">
          <Link href="/" className="inline-block">
            <img src="/images/logo/name.svg" alt="Hummii Logo" className="h-10 md:h-12" />
           {/*
            <Logo variant="fullColor" className="h-10 md:h-12" />
           */}
          </Link>
        </div>

        {/* Copyright */}
        <div className="pt-8 border-t border-background-secondary">
          <Typography variant="bodySm" color="secondary" align="center">
            {t('copyright')}
          </Typography>
        </div>
      </Container>
    </footer>
  );
}
